import { useEffect, useRef, useCallback } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/redux-hooks'
import { loginError } from 'redux/reducers/auth'
import { useNavigate } from 'react-router-dom'
import { getCookie } from 'utils/cokie'
import { Pages } from 'routes/types'
import routes from 'routes'

// Inactivity timeout in milliseconds (6 hours = 21600000 ms)
const INACTIVITY_TIMEOUT = 6 * 60 * 60 * 1000

export const useInactivityTimeout = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { isLoggedIn } = useAppSelector((state) => state.auth)
    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null)
    const warningTimerRef = useRef<NodeJS.Timeout | null>(null)
    const lastActivityRef = useRef<number>(Date.now())

    // Reset the inactivity timer
    const resetInactivityTimer = useCallback(() => {
        if (!isLoggedIn || !getCookie('access_token')) return

        // Clear existing timers
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
        if (warningTimerRef.current) clearTimeout(warningTimerRef.current)

        // Update last activity timestamp
        lastActivityRef.current = Date.now()

        // Show warning at 25 minutes (5 minutes before timeout)
        warningTimerRef.current = setTimeout(() => {
            // You can optionally show a warning modal here
            console.log('[Inactivity] Warning: Session will expire in 5 minutes')
        }, INACTIVITY_TIMEOUT - (5 * 60 * 1000))

        // Logout after 30 minutes of inactivity
        inactivityTimerRef.current = setTimeout(() => {
            console.log('[Inactivity] Session expired due to inactivity')
            handleInactivityLogout()
        }, INACTIVITY_TIMEOUT)
    }, [isLoggedIn])

    // Handle inactivity logout
    const handleInactivityLogout = useCallback(() => {
        dispatch(loginError(null))
        localStorage.setItem('logout_reason', 'Your session has expired due to inactivity. Please log in again.')
        navigate(routes[Pages.Login].path)
    }, [dispatch, navigate])

    // Track user activity
    const handleUserActivity = useCallback(() => {
        resetInactivityTimer()
    }, [resetInactivityTimer])

    // Set up event listeners for user activity
    useEffect(() => {
        if (!isLoggedIn || !getCookie('access_token')) {
            // Clear timers if not logged in or no token
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
            return
        }

        // Initialize timer on first load
        resetInactivityTimer()

        // Activity events to track
        const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']

        // Add event listeners
        activityEvents.forEach((event) => {
            document.addEventListener(event, handleUserActivity, { passive: true })
        })

        // Cleanup
        return () => {
            activityEvents.forEach((event) => {
                document.removeEventListener(event, handleUserActivity)
            })
            if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current)
            if (warningTimerRef.current) clearTimeout(warningTimerRef.current)
        }
    }, [isLoggedIn, resetInactivityTimer, handleUserActivity])

    return { lastActivityTime: lastActivityRef.current }
}
